// @charitystack/schemas — single shared package for Zod schemas across the
// backend monorepo (Commitment 7). Subpaths (./events, ./http, etc.) will be
// added as Stage 2+ ports introduce schemas. The root entrypoint re-exports
// everything for consumers that don't care about tree-shaking; subpath
// imports (e.g. `@charitystack/schemas/marketing/broadcasts`) are also
// available via the `exports` field for narrower imports.

import { z } from 'zod';

export const HealthResponse = z.object({
  ok: z.literal(true),
  service: z.string().min(1),
  stage: z.string().min(1),
  version: z.string().min(1),
  uptime_seconds: z.number().int().nonnegative(),
});

export type HealthResponse = z.infer<typeof HealthResponse>;

// Marketing namespace re-exports. Apps that import the whole namespace get
// `import { broadcastSchema } from '@charitystack/schemas'`; backend code
// that wants narrow imports can use the subpath
// `'@charitystack/schemas/marketing/broadcasts'`.
export * from './marketing/broadcasts.js';

// Internal Inngest command contracts. These cross the bridge/worker boundary,
// so Commitment 7 keeps them in the shared schema package rather than inside
// an individual backend.
export * from './inngest/messaging.js';

// EventBridge event schemas (Stage 3+). Consumers can use the broad
// `'@charitystack/schemas/events'` subpath or narrower per-event subpaths
// for tighter coupling boundaries.
export * from './events/index.js';

// Event Group Ticket v2 wire, persisted ledger, and stable error contracts.
export * from './group-tickets/index.js';

// The frozen PE-1878 country-normalization rule, shared by the org-engagement and payments backend
// units so the stored country label cannot drift between them. Also available at the narrower
// subpath `'@charitystack/schemas/geography/country-normalization'`.
export * from './geography/country-normalization.js';

export * from './marketing/audience-presets.js';

export * from './storage/audience-intent.js';

export * from './marketing/audience-fields.js';
export * from './marketing/native-audience.js';
export * from './marketing/native-audience-preview.js';
